/**
 * Config Registry Filters Component
 * Slide-out drawer with filter options
 */

import React, { useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectConfigRegistryFilters,
  selectUISchema,
  selectConfigTypes,
  selectIsFilterPanelOpen,
  setFilters,
  resetFilters,
  setFilterPanelOpen,
} from '../store/configRegistryUISlice';
import {
  BglPrimarySmallButton,
  BglCloseOutlinedSmallButton,
} from 'components/button/BglButtons';
import { ConfigVerificationStatus, ConfigRegistryFilters as FilterType } from '../types/configRegistryTypes';

const DRAWER_WIDTH = 320;

const STATUS_OPTIONS: { value: ConfigVerificationStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'TODO', label: 'To Do' },
  { value: 'READY_FOR_QA', label: 'Ready for QA' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const RESTRICTED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const ConfigRegistryFiltersDrawer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  
  const filters = useSelector(selectConfigRegistryFilters);
  const uiSchema = useSelector(selectUISchema);
  const configTypes = useSelector(selectConfigTypes);
  const isOpen = useSelector(selectIsFilterPanelOpen);

  // Get lookup options from UI schema
  const getLookupOptions = useCallback((typeCode: string, parentValue?: string) => {
    if (!uiSchema?.lookupTypes) return [];
    const lookupType = uiSchema.lookupTypes.find(lt => lt.code === typeCode);
    if (!lookupType?.lookups) return [];

    // Filter by parent value if provided (for dependent dropdowns)
    if (parentValue && lookupType.dependsOn) {
      return lookupType.lookups.filter(l => l.parentValue === parentValue);
    }
    return lookupType.lookups;
  }, [uiSchema]);

  const handleFilterChange = (field: keyof FilterType, value: string) => {
    const updates: Partial<FilterType> = { [field]: value };

    // Clear dependent filters when parent changes
    // Note: SECTION is independent of COMPONENT (not a child)
    // Only FILE_TYPE depends on CATEGORIZATION
    if (field === 'classification') {
      updates.fileType = '';
    }

    dispatch(setFilters(updates));
  };

  const handleClose = () => dispatch(setFilterPanelOpen(false));
  const handleReset = () => dispatch(resetFilters());

  const componentOptions = getLookupOptions('COMPONENT');
  const sectionOptions = getLookupOptions('SECTION'); // SECTION is independent, not a child of COMPONENT
  const classificationOptions = getLookupOptions('CATEGORIZATION');
  const fileTypeOptions = getLookupOptions('FILE_TYPE', filters.classification);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filters</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Filter Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflowY: 'auto' }}>
          {/* Search */}
          <TextField
            label="Search"
            placeholder="Search by key or name..."
            size="small"
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            fullWidth
          />

          {/* Config Type */}
          <FormControl size="small" fullWidth>
            <InputLabel>Config Type</InputLabel>
            <Select
              value={filters.configType || ''}
              label="Config Type"
              onChange={(e) => handleFilterChange('configType', e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {configTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.verificationStatus || filters.approvalStatus || ''}
              label="Status"
              onChange={(e) => handleFilterChange('verificationStatus', e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Component */}
          <FormControl size="small" fullWidth>
            <InputLabel>Component</InputLabel>
            <Select
              value={filters.component || ''}
              label="Component"
              onChange={(e) => handleFilterChange('component', e.target.value)}
            >
              <MenuItem value="">All Components</MenuItem>
              {componentOptions.map((opt) => (
                <MenuItem key={opt.documentId} value={opt.documentId}>{opt.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Section (independent - no parent) */}
          <FormControl size="small" fullWidth>
            <InputLabel>Section</InputLabel>
            <Select
              value={filters.section || ''}
              label="Section"
              onChange={(e) => handleFilterChange('section', e.target.value)}
            >
              <MenuItem value="">All Sections</MenuItem>
              {sectionOptions.map((opt) => (
                <MenuItem key={opt.documentId} value={opt.documentId}>{opt.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Categorization */}
          <FormControl size="small" fullWidth>
            <InputLabel>Categorization</InputLabel>
            <Select
              value={filters.classification || ''}
              label="Categorization"
              onChange={(e) => handleFilterChange('classification', e.target.value)}
            >
              <MenuItem value="">All Categorizations</MenuItem>
              {classificationOptions.map((opt) => (
                <MenuItem key={opt.documentId} value={opt.documentId}>{opt.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* File Type (depends on Classification) */}
          <FormControl size="small" fullWidth disabled={!filters.classification}>
            <InputLabel>File Type</InputLabel>
            <Select
              value={filters.fileType || ''}
              label="File Type"
              onChange={(e) => handleFilterChange('fileType', e.target.value)}
            >
              <MenuItem value="">All File Types</MenuItem>
              {fileTypeOptions.map((opt) => (
                <MenuItem key={opt.documentId} value={opt.documentId}>{opt.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Restricted */}
          <FormControl size="small" fullWidth>
            <InputLabel>Restricted</InputLabel>
            <Select
              value={filters.restricted || 'all'}
              label="Restricted"
              onChange={(e) => handleFilterChange('restricted', e.target.value as any)}
            >
              {RESTRICTED_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Footer Actions */}
        <Box sx={{ pt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <BglCloseOutlinedSmallButton onClick={handleReset}>
            Reset Filters
          </BglCloseOutlinedSmallButton>
          <BglPrimarySmallButton onClick={handleClose}>
            Apply
          </BglPrimarySmallButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ConfigRegistryFiltersDrawer;

